import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  FileText, 
  ShoppingCart, 
  TrendingUp, 
  XCircle, 
  CheckCircle, 
  Activity,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  Clock
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const statsData = [
  { label: 'Sales Invoices', value: '1,247', change: '+12%', trend: 'up', icon: FileText, color: 'bg-blue-500', bgColor: 'bg-blue-50', borderColor: 'border-l-blue-500' },
  { label: 'Purchase Invoices', value: '892', change: '+8%', trend: 'up', icon: ShoppingCart, color: 'bg-emerald-500', bgColor: 'bg-emerald-50', borderColor: 'border-l-emerald-500' },
  { label: 'Credit Notes', value: '45', change: '-5%', trend: 'down', icon: TrendingUp, color: 'bg-purple-500', bgColor: 'bg-purple-50', borderColor: 'border-l-purple-500' },
  { label: 'Failed Transactions', value: '12', change: '-15%', trend: 'down', icon: XCircle, color: 'bg-rose-500', bgColor: 'bg-rose-50', borderColor: 'border-l-rose-500' },
];

const monthlyData = [
  { month: 'Jan', sales: 98, purchase: 75 },
  { month: 'Feb', sales: 112, purchase: 82 },
  { month: 'Mar', sales: 135, purchase: 95 },
  { month: 'Apr', sales: 148, purchase: 102 },
  { month: 'May', sales: 165, purchase: 118 },
  { month: 'Jun', sales: 178, purchase: 125 },
  { month: 'Jul', sales: 192, purchase: 135 },
  { month: 'Aug', sales: 210, purchase: 148 },
];

const statusData = [
  { name: 'Processed', value: 2087, color: '#10b981' },
  { name: 'Pending', value: 45, color: '#f59e0b' },
  { name: 'Failed', value: 12, color: '#ef4444' },
];

const recentInvoices = [
  { number: 'INV-2024-1247', type: 'Sales', customer: 'Acme Corporation', amount: 15240, status: 'processed', date: '2025-10-24' },
  { number: 'PINV-2024-892', type: 'Purchase', customer: 'Global Supplies LLC', amount: 8750, status: 'processed', date: '2025-10-24' },
  { number: 'INV-2024-1246', type: 'Sales', customer: 'Tech Ventures', amount: 22500, status: 'processed', date: '2025-10-23' },
  { number: 'CN-2024-045', type: 'Credit Note', customer: 'Retail Partners', amount: 1250, status: 'pending', date: '2025-10-23' },
  { number: 'PINV-2024-891', type: 'Purchase', customer: 'Office Supplies Co', amount: 3480, status: 'failed', date: '2025-10-22' },
];

export default function ClientOverview() {
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'processed':
        return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Processed</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-rose-100 text-rose-700 border-rose-200 hover:bg-rose-100">Failed</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-mesh-gradient min-h-screen">
      {/* Header */}
      <div className="flex items-start justify-between fade-in">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-xl blur-md opacity-50"></div>
            <div className="relative p-3 bg-gradient-to-br from-[#0052CC] to-indigo-600 rounded-xl premium-shadow">
              <Activity className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-4xl text-gray-900">Dashboard Overview</h1>
            <p className="text-gray-600 mt-1 flex items-center gap-2">
              <span>UAE E-Invoicing Portal</span>
              <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle className="w-3 h-3 mr-1" />
                FTA Compliant
              </Badge>
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 slide-up">
        {statsData.map((stat, index) => {
          const Icon = stat.icon;
          const isPositive = stat.trend === 'up';
          return (
            <Card 
              key={stat.label} 
              className={`relative overflow-hidden border-l-4 ${stat.borderColor} premium-shadow hover-lift group`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`absolute top-0 right-0 w-32 h-32 ${stat.bgColor} rounded-full -translate-y-16 translate-x-16 opacity-30 group-hover:scale-150 transition-transform duration-500`}></div>
              <CardContent className="p-6 relative z-10">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-sm text-gray-600 mb-1 uppercase tracking-wide">{stat.label}</p>
                    <p className="text-4xl text-gray-900 mt-2">{stat.value}</p>
                    <div className={`flex items-center mt-3 ${isPositive ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {isPositive ? (
                        <ArrowUpRight className="w-4 h-4 mr-1" />
                      ) : (
                        <ArrowDownRight className="w-4 h-4 mr-1" />
                      )}
                      <span className="text-sm">{stat.change}</span>
                      <span className="text-xs text-gray-500 ml-1">vs last month</span>
                    </div>
                  </div>
                  <div className={`p-4 ${stat.bgColor} rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`w-6 h-6 ${stat.color.replace('bg-', 'text-')}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Invoices Chart */}
        <Card className="premium-shadow hover-lift overflow-hidden">
          <CardHeader className="border-b bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <BarChart3 className="w-5 h-5 text-[#0052CC]" />
              </div>
              <div>
                <CardTitle className="text-xl">Monthly Invoice Trends</CardTitle>
                <CardDescription className="mt-1">Sales and purchase invoice comparison for 2025</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-8 pb-6">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#6b7280' }}
                  tickLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fill: '#6b7280' }}
                  tickLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffffff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="sales" 
                  stroke="#0052CC" 
                  strokeWidth={3} 
                  name="Sales Invoices"
                  dot={{ fill: '#0052CC', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="purchase" 
                  stroke="#10b981" 
                  strokeWidth={3} 
                  name="Purchase Invoices"
                  dot={{ fill: '#10b981', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card className="premium-shadow hover-lift overflow-hidden">
          <CardHeader className="border-b bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Transaction Status</CardTitle>
                <CardDescription className="mt-1">Real-time distribution overview</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-8 pb-6">
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={90}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={2}
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#ffffff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-6">
              {statusData.map((item) => (
                <div key={item.name} className="relative text-center p-4 bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-100 hover:shadow-md transition-all duration-300 group">
                  <div className="w-4 h-4 rounded-full mx-auto mb-2 group-hover:scale-125 transition-transform" style={{ backgroundColor: item.color }}></div>
                  <p className="text-xs text-gray-600 mb-1 uppercase tracking-wide">{item.name}</p>
                  <p className="text-2xl text-gray-900">{item.value.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="premium-shadow-lg hover-lift overflow-hidden">
        <CardHeader className="border-b bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Recent Transactions</CardTitle>
                <CardDescription className="mt-1">Latest invoice activity across all categories</CardDescription>
              </div>
            </div>
            <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0">
              Live Updates
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {recentInvoices.map((invoice, index) => (
              <div 
                key={invoice.number} 
                className="p-5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 group cursor-pointer"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 rounded-xl shadow-sm group-hover:scale-110 transition-transform ${
                      invoice.status === 'processed' ? 'bg-gradient-to-br from-emerald-50 to-green-100' : 
                      invoice.status === 'pending' ? 'bg-gradient-to-br from-amber-50 to-orange-100' : 'bg-gradient-to-br from-rose-50 to-red-100'
                    }`}>
                      {invoice.status === 'processed' && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                      {invoice.status === 'pending' && <Clock className="w-5 h-5 text-amber-600" />}
                      {invoice.status === 'failed' && <XCircle className="w-5 h-5 text-rose-600" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <p className="text-[#0052CC] group-hover:text-indigo-600 transition-colors">{invoice.number}</p>
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">{invoice.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 group-hover:text-gray-900">{invoice.customer}</p>
                      <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {invoice.date}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl text-gray-900 mb-2">AED {invoice.amount.toLocaleString()}</p>
                    {getStatusBadge(invoice.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
